/**
 * Client-side type compatibility checker for instant validation feedback.
 *
 * Ports the 4 single-field checks from Python's type_compatibility_validator.py
 * so the UI can show warnings immediately when a user changes a source field,
 * without waiting for a backend round-trip.
 *
 * When an expression is present and has been analysed by the backend (sqlglot),
 * the `expressionOutputType` / `hasCoalesce` params override raw source type
 * to give accurate post-transformation warnings.
 */

// ── Type families ────────────────────────────────────────────────────

const NUMERIC_TYPES = new Set([
  'int', 'integer', 'float', 'double', 'decimal', 'number', 'numeric',
  'bigint', 'smallint', 'tinyint',
]);

const STRING_TYPES = new Set([
  'str', 'string', 'varchar', 'text', 'char', 'nvarchar', 'nchar',
]);

const DATE_TYPES = new Set([
  'date', 'datetime', 'timestamp', 'time', 'datetime2', 'datetimeoffset',
]);

const BOOL_TYPES = new Set(['bool', 'boolean', 'bit']);

// ── Helpers ──────────────────────────────────────────────────────────

export function normaliseType(raw?: string): string {
  if (!raw) return '';
  return raw.replace(/\(.*\)/, '').toLowerCase().trim();
}

export function parseLength(raw?: string): number | null {
  if (!raw) return null;
  const m = raw.match(/\((\d+)\)/);
  return m ? parseInt(m[1], 10) : null;
}

export function parsePrecisionScale(raw?: string): [number | null, number | null] {
  if (!raw) return [null, null];
  const m = raw.match(/\((\d+)\s*,\s*(\d+)\)/);
  return m ? [parseInt(m[1], 10), parseInt(m[2], 10)] : [null, null];
}

export function typeFamily(normalised: string): string | null {
  if (NUMERIC_TYPES.has(normalised)) return 'numeric';
  if (STRING_TYPES.has(normalised)) return 'string';
  if (DATE_TYPES.has(normalised)) return 'date';
  if (BOOL_TYPES.has(normalised)) return 'boolean';
  return null;
}

// ── Public types ─────────────────────────────────────────────────────

export type TypeRuleType =
  | 'truncation_risk'
  | 'precision_loss'
  | 'type_coercion'
  | 'nullable_mismatch';

export interface SuggestedFix {
  label: string;
  fixType: string;
  expression: string;
}

export interface TypeCompatibilityIssue {
  ruleType: TypeRuleType;
  severity: 'error' | 'warning';
  message: string;
  remediation: string;
  suggestedFix?: SuggestedFix;
}

export interface TypeCheckParams {
  sourceField?: string;
  sourceType?: string;
  targetField?: string;
  targetType?: string;
  sourceNullable?: boolean;
  targetNullable?: boolean;
  transformation?: string;
  transformationType?: string;
  /** From backend sqlglot analysis — overrides source type when CAST detected */
  expressionOutputType?: string;
  /** From backend analysis — suppresses nullable warning when COALESCE detected */
  hasCoalesce?: boolean;
  /** Whether a default value is set */
  hasDefaultValue?: boolean;
}

// ── Main checker ─────────────────────────────────────────────────────

export function evaluateFieldTypeCompatibility(params: TypeCheckParams): TypeCompatibilityIssue[] {
  const {
    sourceField = '',
    sourceType = '',
    targetField = '',
    targetType = '',
    sourceNullable = true,
    targetNullable = true,
    transformation = '',
    transformationType = 'direct',
    expressionOutputType,
    hasCoalesce = false,
    hasDefaultValue = false,
  } = params;

  const issues: TypeCompatibilityIssue[] = [];

  const targetTypeNorm = normaliseType(targetType);
  const sourceTypeNorm = normaliseType(sourceType);

  // Effective source type: use expression output if available
  const effectiveTypeRaw = expressionOutputType || sourceType;
  const effectiveTypeNorm = normaliseType(effectiveTypeRaw);

  if (!targetTypeNorm) return issues; // No target type info → nothing to check

  // ── Check 1: Truncation risk (string length) ────────────────────
  const targetLen = parseLength(targetType);
  const sourceLen = parseLength(sourceType);
  if (
    targetLen !== null &&
    sourceLen !== null &&
    targetLen < sourceLen &&
    typeFamily(targetTypeNorm) === 'string'
  ) {
    issues.push({
      ruleType: 'truncation_risk',
      severity: 'warning',
      message: `Possible truncation: source ${sourceType} \u2192 target ${targetType}`,
      remediation: `Add SUBSTRING(${sourceField}, 1, ${targetLen}) to prevent silent truncation`,
      suggestedFix: {
        label: 'Add SUBSTRING',
        fixType: 'substring',
        expression: `SUBSTRING(${sourceField}, 1, ${targetLen})`,
      },
    });
  }

  // ── Check 2: Precision loss (decimal) ───────────────────────────
  const [targetPrec, targetScale] = parsePrecisionScale(targetType);
  const [sourcePrec, sourceScale] = parsePrecisionScale(sourceType);
  if (
    targetPrec !== null &&
    sourcePrec !== null &&
    (targetPrec < sourcePrec ||
      (targetScale !== null && sourceScale !== null && targetScale < sourceScale))
  ) {
    issues.push({
      ruleType: 'precision_loss',
      severity: 'warning',
      message: `Precision loss: source ${sourceType} \u2192 target ${targetType}`,
      remediation: 'Add explicit ROUND or CAST to control precision',
      suggestedFix: {
        label: 'Add ROUND',
        fixType: 'round',
        expression: `ROUND(${sourceField}, ${targetScale ?? 2})`,
      },
    });
  }

  // ── Check 3: Type coercion without CAST ─────────────────────────
  // Use effective type (post-CAST) for comparison
  if (
    effectiveTypeNorm &&
    targetTypeNorm &&
    effectiveTypeNorm !== targetTypeNorm &&
    typeFamily(effectiveTypeNorm) !== typeFamily(targetTypeNorm) &&
    typeFamily(targetTypeNorm) !== 'string' && // strings accept anything
    !expressionOutputType && // if we have output type, CAST was detected
    transformationType === 'direct'
  ) {
    issues.push({
      ruleType: 'type_coercion',
      severity: 'warning',
      message: `Implicit type coercion: ${sourceField} (${sourceType || sourceTypeNorm}) \u2192 ${targetField} (${targetType || targetTypeNorm}) without CAST`,
      remediation: `Add CAST(${sourceField} AS ${targetType || targetTypeNorm}) for explicit conversion`,
      suggestedFix: {
        label: 'Add CAST',
        fixType: 'cast',
        expression: `CAST(${sourceField} AS ${targetType || targetTypeNorm})`,
      },
    });
  }

  // ── Check 4: Nullable → NonNullable without COALESCE ────────────
  if (
    !targetNullable &&
    sourceNullable &&
    !hasCoalesce &&
    !hasDefaultValue
  ) {
    issues.push({
      ruleType: 'nullable_mismatch',
      severity: 'warning',
      message: `Source '${sourceField}' may contain nulls but target '${targetField}' is NOT NULL`,
      remediation: `Add COALESCE(${sourceField}, <default_value>) or set a default value`,
      suggestedFix: {
        label: 'Add COALESCE',
        fixType: 'coalesce',
        expression: typeFamily(targetTypeNorm) === 'string'
          ? `COALESCE(${sourceField}, '')`
          : typeFamily(targetTypeNorm) === 'numeric'
            ? `COALESCE(${sourceField}, 0)`
            : `COALESCE(${sourceField}, NULL)`,
      },
    });
  }

  return issues;
}
